import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivityService } from '@app/_components/activity';
import { ACTIVITY_TYPES } from '@app/_components/activity/activity-constants';
import { ActivityProviderService } from '@app/_components/activity-provider/activity-provider.service';
import { ActivityReservationService } from '@app/_components/activity-reservation';
import { ActivityWishlistService } from '@app/_components/activity-wishlist/activity-wishlist.service';
import { AlertService } from '@app/_services';
import { first } from 'rxjs/operators';
import { ActivityFormComponent } from '../activity/activity-form/activity-form.component';
import { ActivityProviderFormComponent } from '../activity-provider/activity-provider-form/activity-provider-form.component';
import { ActivityReservationFormComponent } from '../activity-reservation/activity-reservation-form/activity-reservation-form.component';

@Component({
	selector: 'app-activities-manager',
	templateUrl: './activities-manager.component.html',
	styleUrls: ['./activities-manager.component.scss'],
	standalone: false,
})
export class ActivitiesManagerComponent implements OnInit {

	activeTab: string = 'activities';

	// Data
	activities: any[] = [];
	providers: any[] = [];
	reservations: any[] = [];
	categories: any[] = [];

	// Stats
	totalActivities: number = 0;
	activeCount: number = 0;
	draftCount: number = 0;
	reservationCount: number = 0;
	unpaidCount: number = 0;

	// Toast
	toastMsg: string = '';
	toastVisible: boolean = false;
	private toastTimer: any;

	// Duplicate loading tracker
	duplicatingId: number | null = null;

	// Activity filters
	actSearch: string = '';
	actFilterCat: string = '';
	actFilterProp: string = '';
	actFilterStatus: string = '';

	// Reports
	reportDateFrom: string = '';
	reportDateTo: string = '';
	reportGroupBy: string = 'activity';
	reportView: string = 'overview';
	readonly COMM_RATE = 0.10;

	readonly TABS = [
		{ id: 'activities',   label: 'Activities' },
		{ id: 'reservations', label: 'Reservations' },
		{ id: 'providers',    label: 'Providers' },
		{ id: 'categories',   label: 'Categories & Types' },
		{ id: 'reports',      label: 'Reports' },
		{ id: 'wishlists',    label: 'Wish Lists' },
	];

	// Wish Lists
	wishlists: any[] = [];
	wishlistsLoading = false;
	selectedWishlist: any = null;
	showWishlistDetail = false;
	showCreateWishlist = false;
	wishlistStatusFilter = '';
	wishlistActivitySearch = '';
	wishlistSaving = false;
	wishlistSending = false;
	newWishlist: any = {};

	readonly WISHLIST_STATUSES = [
		{ value: '',           label: 'All' },
		{ value: 'submitted',  label: 'Submitted' },
		{ value: 'suggested',  label: 'Suggested' },
		{ value: 'viewed',     label: 'Viewed' },
		{ value: 'confirmed',  label: 'Confirmed' },
		{ value: 'processing', label: 'Processing' },
		{ value: 'completed',  label: 'Completed' },
	];

	readonly ACTIVITY_TYPES = ACTIVITY_TYPES;

	get CATEGORIES() { return this.activityService.categories; }

	/** Unique property names derived from actual loaded activities */
	get PROPERTIES(): string[] {
		const seen = new Set<string>();
		this.activities.forEach(a => {
			if (a.properties) {
				a.properties.split(',').forEach((p: string) => {
					const t = p.trim();
					if (t) seen.add(t);
				});
			}
		});
		return Array.from(seen).sort();
	}

	constructor(
		private activityService: ActivityService,
		private activityProviderService: ActivityProviderService,
		private activityReservationService: ActivityReservationService,
		private activityWishlistService: ActivityWishlistService,
		private alertService: AlertService,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		const now = new Date();
		const y = now.getFullYear();
		this.reportDateFrom = `${y}-01-01`;
		this.reportDateTo   = `${y}-12-31`;
		this.newWishlist = this.blankNewWishlist();
		this.loadActivities();
		this.loadProviders();
		this.loadReservations();
		this.activityService.loadProperties();
	}

	/* ── Data Loaders ── */
	loadActivities() {
		this.activityService.findAll().pipe(first()).subscribe(
			res => {
				this.activities = res.data.activitys || [];
				this.computeStats();
			},
			err => this.alertService.error(err)
		);
	}

	loadProviders() {
		this.activityProviderService.findAll().pipe(first()).subscribe(
			res => { this.providers = res.data.activityProviders || []; },
			err => this.alertService.error(err)
		);
	}

	loadReservations() {
		this.activityReservationService.findAll().pipe(first()).subscribe(
			res => {
				this.reservations = res.data.activityReservations || [];
				this.computeStats();
			},
			err => this.alertService.error(err)
		);
	}

	computeStats() {
		this.totalActivities  = this.activities.length;
		this.activeCount      = this.activities.filter(a => a.status === 'active').length;
		this.draftCount       = this.activities.filter(a => a.status === 'draft').length;
		this.reservationCount = this.reservations.length;
		this.unpaidCount      = this.reservations.filter(r => r.paymentStatus === 'unpaid').length;
	}

	/* ── Activity Filtering ── */
	get filteredActivities() {
		return this.activities.filter(a => {
			const q = this.actSearch.toLowerCase().trim();
			if (q) {
				const nameMatch     = a.name?.toLowerCase().includes(q);
				const provMatch     = this.providerName(a.activityProviderId)?.toLowerCase().includes(q);
				const typeMatch     = a.activityType?.toLowerCase().includes(q);
				if (!nameMatch && !provMatch && !typeMatch) return false;
			}
			if (this.actFilterCat) {
				const cat = (a.category || '').toLowerCase().trim();
				if (cat !== this.actFilterCat.toLowerCase()) return false;
			}
			if (this.actFilterProp) {
				const actProps = (a.properties || '').split(',').map((p: string) => p.trim().toLowerCase());
				if (!actProps.includes(this.actFilterProp.toLowerCase())) return false;
			}
			if (this.actFilterStatus) {
				const status = (a.status || '').toLowerCase().trim();
				if (status !== this.actFilterStatus.toLowerCase()) return false;
			}
			return true;
		});
	}

	clearActFilters() {
		this.actSearch = '';
		this.actFilterCat = '';
		this.actFilterProp = '';
		this.actFilterStatus = '';
	}

	/* ── Providers helper ── */
	providerName(id: number): string {
		return this.providers.find(p => p.activityProviderId === id)?.name || '';
	}

	getCategoryInfo(cat: string): { label: string; cssClass: string } {
		const found = this.CATEGORIES.find((c: any) => c.value === cat);
		if (found) return { label: found.name, cssClass: found.cssClass || '' };
		return { label: cat, cssClass: '' };
	}

	/* ── Reports Computed ── */
	get reportInRange() {
		return this.reservations.filter(r =>
			r.activityDate &&
			r.activityDate >= this.reportDateFrom &&
			r.activityDate <= this.reportDateTo
		);
	}

	get reportTotalRevenue()   { return this.reportInRange.reduce((s, r) => s + (r.amountDue || 0), 0); }
	get reportTotalCollected() { return this.reportInRange.reduce((s, r) => s + (r.amountPaid || 0), 0); }
	get reportTotalBalance()   { return this.reportTotalRevenue - this.reportTotalCollected; }
	get reportTotalGuests()    { return this.reportInRange.reduce((s, r) => s + (r.numGuests || 0), 0); }
	get reportTotalActsSold()  { return this.reportInRange.reduce((s, r) => s + (r.activityIds?.split(',').filter(Boolean).length || 0), 0); }
	get reportTotalComm()      { return this.reportTotalRevenue * this.COMM_RATE; }
	get reportAvgPerRes()      { return this.reportInRange.length ? this.reportTotalRevenue / this.reportInRange.length : 0; }
	get reportCollRate()       { return this.reportTotalRevenue > 0 ? Math.round(this.reportTotalCollected / this.reportTotalRevenue * 100) : 0; }

	get reportByActivity() {
		const map: any = {};
		this.reportInRange.forEach(r => {
			const ids = (r.activityIds || '').split(',').filter(Boolean);
			ids.forEach((aid: string) => {
				const act = this.activities.find(a => String(a.activityId) === aid.trim());
				if (!act) return;
				if (!map[aid]) map[aid] = { name: act.name, emoji: act.emoji, cat: act.category, count: 0, revenue: 0, guests: 0, comm: 0 };
				const share = (r.amountDue || 0) / (ids.length || 1);
				map[aid].count++;
				map[aid].revenue += share;
				map[aid].guests  += r.numGuests || 0;
				map[aid].comm    += share * this.COMM_RATE;
			});
		});
		return Object.values(map).sort((a: any, b: any) => b.revenue - a.revenue);
	}

	get reportByCategory() {
		const map: any = {};
		(this.reportByActivity as any[]).forEach(a => {
			const cat = a.cat || 'other';
			if (!map[cat]) map[cat] = { name: this.getCategoryInfo(cat).label, cat, count: 0, revenue: 0, guests: 0, comm: 0 };
			map[cat].count   += a.count;
			map[cat].revenue += a.revenue;
			map[cat].guests  += a.guests;
			map[cat].comm    += a.comm;
		});
		return Object.values(map).sort((a: any, b: any) => b.revenue - a.revenue);
	}

	get reportByProperty() {
		const map: any = {};
		this.reportInRange.forEach(r => {
			const key = r.property || 'Unknown';
			if (!map[key]) map[key] = { name: key, count: 0, revenue: 0, guests: 0, comm: 0 };
			map[key].count   += (r.activityIds || '').split(',').filter(Boolean).length;
			map[key].revenue += r.amountDue || 0;
			map[key].guests  += r.numGuests || 0;
			map[key].comm    += (r.amountDue || 0) * this.COMM_RATE;
		});
		return Object.values(map).sort((a: any, b: any) => b.revenue - a.revenue);
	}

	get reportByProvider() {
		const map: any = {};
		this.reportInRange.forEach(r => {
			const ids = (r.activityIds || '').split(',').filter(Boolean);
			ids.forEach((aid: string) => {
				const act  = this.activities.find(a => String(a.activityId) === aid.trim());
				const prov = this.providers.find(p => p.activityProviderId === act?.activityProviderId);
				const key  = prov?.name || 'Unknown';
				if (!map[key]) map[key] = { name: key, count: 0, revenue: 0, guests: 0, comm: 0 };
				const share = (r.amountDue || 0) / (ids.length || 1);
				map[key].count++;
				map[key].revenue += share;
				map[key].guests  += r.numGuests || 0;
				map[key].comm    += share * this.COMM_RATE;
			});
		});
		return Object.values(map).sort((a: any, b: any) => b.revenue - a.revenue);
	}

	get reportActiveTable() {
		if (this.reportGroupBy === 'activity')  return this.reportByActivity;
		if (this.reportGroupBy === 'category')  return this.reportByCategory;
		if (this.reportGroupBy === 'property')  return this.reportByProperty;
		return this.reportByProvider;
	}

	get reportGroupLabel() {
		return { activity: 'Activity', category: 'Category', property: 'Property', provider: 'Provider' }[this.reportGroupBy] || '';
	}

	get reportPayBreakdown() {
		return [
			{ name: 'Paid',     value: this.reportInRange.filter(r => r.paymentStatus === 'paid').length,     color: '#10b981' },
			{ name: 'Partial',  value: this.reportInRange.filter(r => r.paymentStatus === 'partial').length,  color: '#f59e0b' },
			{ name: 'Unpaid',   value: this.reportInRange.filter(r => r.paymentStatus === 'unpaid').length,   color: '#ef4444' },
			{ name: 'Refunded', value: this.reportInRange.filter(r => r.paymentStatus === 'refunded').length, color: '#8b5cf6' },
		].filter(x => x.value > 0);
	}

	get reportMonthlyTrend() {
		const map: any = {};
		this.reportInRange.forEach(r => {
			if (!r.activityDate) return;
			const mo = String(r.activityDate).slice(0, 7);
			if (!map[mo]) map[mo] = { month: mo, revenue: 0, collected: 0, count: 0, comm: 0 };
			map[mo].revenue   += r.amountDue || 0;
			map[mo].collected += r.amountPaid || 0;
			map[mo].count     += (r.activityIds || '').split(',').filter(Boolean).length;
			map[mo].comm      += (r.amountDue || 0) * this.COMM_RATE;
		});
		const months = Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]: any) => ({
			...v,
			label:     new Date(k + '-02').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
			revenue:   Math.round(v.revenue),
			collected: Math.round(v.collected),
			comm:      Math.round(v.comm),
		}));
		return months;
	}

	barHeight(value: number, max: number): string {
		if (!max || max === 0) return '0%';
		return Math.round((value / max) * 100) + '%';
	}

	barWidth(value: number, data: any[], key: string): string {
		const max = Math.max(...data.map((d: any) => d[key] || 0));
		if (!max) return '3%';
		return Math.max(Math.round((value / max) * 100), 3) + '%';
	}

	readonly BAR_COLORS = ['#10b981','#3b82f6','#f59e0b','#8b5cf6','#f43f5e','#06b6d4','#84cc16','#fb923c'];

	/* ── Modal Openers ── */
	openNewActivity() {
		const cfg = new MatDialogConfig();
		cfg.panelClass  = 'kbm-dialog-panel';
		cfg.maxWidth    = '680px';
		cfg.width       = '100%';
		cfg.data = { title: 'New Activity', activity: null };
		const ref = this.dialog.open(ActivityFormComponent, cfg);
		ref.componentInstance.doAdd.subscribe((a: any) => {
			this.activities.unshift(a);
			this.activities = [...this.activities];
			this.computeStats();
			if (a._savedStatus !== 'draft') {
				this.showToast('Activity published.');
				ref.close();
			} else {
				this.showToast('Draft saved.');
			}
		});
	}

	openEditActivity(activity: any) {
		const cfg = new MatDialogConfig();
		cfg.panelClass  = 'kbm-dialog-panel';
		cfg.maxWidth    = '680px';
		cfg.width       = '100%';
		cfg.data = { title: `Edit: ${activity.name}`, activity };
		const ref = this.dialog.open(ActivityFormComponent, cfg);
		ref.componentInstance.doUpdate.subscribe((a: any) => {
			const i = this.activities.findIndex(x => x.activityId === a.activityId);
			if (i > -1) this.activities[i] = a;
			this.activities = [...this.activities];
			this.computeStats();
			if (a._savedStatus !== 'draft') {
				this.showToast('Activity updated.');
				ref.close();
			} else {
				this.showToast('Draft saved.');
			}
		});
		ref.componentInstance.doDelete.subscribe((a: any) => {
			const i = this.activities.findIndex(x => x.activityId === a.activityId);
			if (i > -1) this.activities[i] = a;
			this.activities = [...this.activities];
			this.computeStats();
			this.showToast('Activity archived.');
			ref.close();
		});
	}

	duplicateActivity(activity: any) {
		if (this.duplicatingId !== null) return;
		this.duplicatingId = activity.activityId;
		const copy = { ...activity, activityId: undefined, name: activity.name + ' (copy)', status: 'draft' };
		this.activityService.add(copy).pipe(first()).subscribe(
			res => {
				this.activities.unshift(res.data.activity);
				this.activities = [...this.activities];
				this.computeStats();
				this.showToast('Activity duplicated as draft.');
				this.duplicatingId = null;
			},
			err => {
				this.alertService.error(err);
				this.duplicatingId = null;
			}
		);
	}

	openNewProvider() {
		const cfg = new MatDialogConfig();
		cfg.panelClass  = 'kbm-dialog-panel';
		cfg.maxWidth    = '580px';
		cfg.width       = '100%';
		cfg.data = { title: 'Add Provider', activityProvider: null };
		const ref = this.dialog.open(ActivityProviderFormComponent, cfg);
		ref.componentInstance.doAdd.subscribe((p: any) => {
			this.providers.unshift(p);
			this.providers = [...this.providers];
			this.showToast('Provider saved.');
			ref.close();
		});
	}

	openEditProvider(provider: any) {
		const cfg = new MatDialogConfig();
		cfg.panelClass  = 'kbm-dialog-panel';
		cfg.maxWidth    = '580px';
		cfg.width       = '100%';
		cfg.data = { title: `Edit: ${provider.name}`, activityProvider: provider };
		const ref = this.dialog.open(ActivityProviderFormComponent, cfg);
		ref.componentInstance.doUpdate.subscribe((p: any) => {
			const i = this.providers.findIndex(x => x.activityProviderId === p.activityProviderId);
			if (i > -1) this.providers[i] = p;
			this.providers = [...this.providers];
			this.showToast('Provider updated.');
			ref.close();
		});
	}

	openNewReservation() {
		const cfg = new MatDialogConfig();
		cfg.panelClass  = 'kbm-dialog-panel';
		cfg.maxWidth    = '680px';
		cfg.width       = '100%';
		cfg.data = { title: 'New Reservation', reservation: null, activities: this.activities };
		const ref = this.dialog.open(ActivityReservationFormComponent, cfg);
		ref.componentInstance.doAdd.subscribe((r: any) => {
			this.reservations.unshift(r);
			this.reservations = [...this.reservations];
			this.computeStats();
			this.showToast('Reservation created.');
			ref.close();
		});
	}

	openEditReservation(reservation: any) {
		const cfg = new MatDialogConfig();
		cfg.panelClass  = 'kbm-dialog-panel';
		cfg.maxWidth    = '680px';
		cfg.width       = '100%';
		cfg.data = { title: `${reservation.firstName} ${reservation.lastName}`, reservation, activities: this.activities };
		const ref = this.dialog.open(ActivityReservationFormComponent, cfg);
		ref.componentInstance.doUpdate.subscribe((r: any) => {
			const i = this.reservations.findIndex(x => x.activityReservationId === r.activityReservationId);
			if (i > -1) this.reservations[i] = r;
			this.reservations = [...this.reservations];
			this.computeStats();
			this.showToast('Reservation updated.');
			ref.close();
		});
	}

	/* ── Wish Lists ── */
	blankNewWishlist(): any {
		return {
			guestFirstName: '',
			guestLastName: '',
			guestEmail: '',
			guestPhone: '',
			reservationId: null,
			propertyName: '',
			checkIn: '',
			checkOut: '',
			addressIsland: '',
			staffNotes: '',
			status: 'suggested',
			items: [],
		};
	}

	get filteredWishlists(): any[] {
		if (!this.wishlistStatusFilter) return this.wishlists;
		return this.wishlists.filter(wl => wl.status === this.wishlistStatusFilter);
	}

	loadWishlists() {
		this.wishlistsLoading = true;
		this.activityWishlistService.findAll({ page: 0, size: 200 }).pipe(first()).subscribe(
			res => {
				const raw: any[] = res.data?.content || res.data?.wishlists || [];
				this.wishlists = raw.map(wl => this.parseWishlistItems(wl));
				this.wishlistsLoading = false;
			},
			err => {
				this.alertService.error(err);
				this.wishlistsLoading = false;
			}
		);
	}

	parseWishlistItems(wl: any): any {
		try {
			wl.items = wl.itemsJson ? JSON.parse(wl.itemsJson) : [];
		} catch {
			wl.items = [];
		}
		return wl;
	}

	onWishlistTabActivate() {
		if (!this.wishlists.length && !this.wishlistsLoading) {
			this.loadWishlists();
		}
	}

	onWishlistRowClick(wl: any) {
		this.selectedWishlist = JSON.parse(JSON.stringify(wl));
		this.showWishlistDetail = true;
	}

	closeWishlistDetail() {
		this.showWishlistDetail = false;
		this.selectedWishlist = null;
	}

	saveWishlistChanges() {
		if (!this.selectedWishlist) return;
		this.wishlistSaving = true;
		const payload = {
			...this.selectedWishlist,
			itemsJson: JSON.stringify(this.selectedWishlist.items || []),
		};
		this.activityWishlistService.update(payload).pipe(first()).subscribe(
			res => {
				const updated = this.parseWishlistItems(res.data?.wishlist || payload);
				const i = this.wishlists.findIndex(w => w.wishlistId === updated.wishlistId);
				if (i > -1) this.wishlists[i] = updated;
				this.wishlists = [...this.wishlists];
				this.selectedWishlist = JSON.parse(JSON.stringify(updated));
				this.wishlistSaving = false;
				this.showToast('Wish list saved.');
			},
			err => {
				this.alertService.error(err);
				this.wishlistSaving = false;
			}
		);
	}

	sendToGuest(wl: any) {
		this.wishlistSending = true;
		this.activityWishlistService.sendToGuest(wl.wishlistId).pipe(first()).subscribe(
			res => {
				const updated = this.parseWishlistItems(res.data?.wishlist || wl);
				const i = this.wishlists.findIndex(w => w.wishlistId === updated.wishlistId);
				if (i > -1) this.wishlists[i] = updated;
				this.wishlists = [...this.wishlists];
				if (this.selectedWishlist?.wishlistId === updated.wishlistId) {
					this.selectedWishlist = JSON.parse(JSON.stringify(updated));
				}
				this.wishlistSending = false;
				this.showToast('Wish list sent to guest.');
			},
			err => {
				this.alertService.error(err);
				this.wishlistSending = false;
			}
		);
	}

	deleteWishlist(wl: any) {
		if (!confirm(`Delete wish list for ${wl.guestFirstName} ${wl.guestLastName}?`)) return;
		this.activityWishlistService.delete(wl.wishlistId).pipe(first()).subscribe(
			() => {
				this.wishlists = this.wishlists.filter(w => w.wishlistId !== wl.wishlistId);
				if (this.selectedWishlist?.wishlistId === wl.wishlistId) {
					this.showWishlistDetail = false;
					this.selectedWishlist = null;
				}
				this.showToast('Wish list deleted.');
			},
			err => this.alertService.error(err)
		);
	}

	openCreateWishlist() {
		this.newWishlist = this.blankNewWishlist();
		this.wishlistActivitySearch = '';
		this.showCreateWishlist = true;
	}

	closeCreateWishlist() {
		this.showCreateWishlist = false;
	}

	get newWishlistFilteredActivities(): any[] {
		const q = this.wishlistActivitySearch.toLowerCase().trim();
		if (!q) return this.activities.filter(a => a.status === 'active').slice(0, 30);
		return this.activities.filter(a =>
			a.status === 'active' &&
			(a.name?.toLowerCase().includes(q) || a.category?.toLowerCase().includes(q))
		).slice(0, 30);
	}

	addActivityToNewWishlist(activity: any) {
		const item = {
			activityId: activity.activityId,
			activityName: activity.name,
			coverImageUrl: activity.coverImageUrl || '',
			category: activity.category || '',
			preferredDate: '',
			preferredTime: '',
			adultCount: 2,
			youthCount: 0,
			infantCount: 0,
			guestNotes: '',
			staffNote: '',
		};
		this.newWishlist.items = [...(this.newWishlist.items || []), item];
		this.wishlistActivitySearch = '';
	}

	removeNewWishlistItem(index: number) {
		this.newWishlist.items = this.newWishlist.items.filter((_: any, i: number) => i !== index);
	}

	addActivityToSelectedWishlist(activity: any) {
		if (!this.selectedWishlist) return;
		const item = {
			activityId: activity.activityId,
			activityName: activity.name,
			coverImageUrl: activity.coverImageUrl || '',
			category: activity.category || '',
			preferredDate: '',
			preferredTime: '',
			adultCount: 2,
			youthCount: 0,
			infantCount: 0,
			guestNotes: '',
			staffNote: '',
		};
		this.selectedWishlist.items = [...(this.selectedWishlist.items || []), item];
	}

	removeSelectedWishlistItem(index: number) {
		if (!this.selectedWishlist) return;
		this.selectedWishlist.items = this.selectedWishlist.items.filter((_: any, i: number) => i !== index);
	}

	saveNewWishlist(andSend: boolean = false) {
		this.wishlistSaving = true;
		const payload = {
			...this.newWishlist,
			itemsJson: JSON.stringify(this.newWishlist.items || []),
		};
		this.activityWishlistService.create(payload).pipe(first()).subscribe(
			res => {
				const created = this.parseWishlistItems(res.data?.wishlist || payload);
				this.wishlists = [created, ...this.wishlists];
				this.wishlistSaving = false;
				this.showCreateWishlist = false;
				this.showToast('Wish list created.');
				if (andSend && created.wishlistId) {
					this.sendToGuest(created);
				}
			},
			err => {
				this.alertService.error(err);
				this.wishlistSaving = false;
			}
		);
	}

	wishlistStatusBadgeClass(status: string): string {
		const map: any = {
			submitted:  'badge-wl-submitted',
			suggested:  'badge-wl-suggested',
			viewed:     'badge-wl-viewed',
			confirmed:  'badge-wl-confirmed',
			processing: 'badge-wl-processing',
			completed:  'badge-wl-completed',
		};
		return map[status] || '';
	}

	/* ── Toast ── */
	showToast(msg: string) {
		this.toastMsg = msg;
		this.toastVisible = true;
		clearTimeout(this.toastTimer);
		this.toastTimer = setTimeout(() => { this.toastVisible = false; }, 3000);
	}

	/* ── Formatters ── */
	fmt$(n: number): string {
		return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	}

	fmtDate(s: string): string {
		if (!s) return '—';
		const [y, m, d] = s.split('-');
		return `${m}/${d}/${y}`;
	}

	activityNames(activityIds: string): string {
		if (!activityIds) return '—';
		return activityIds.split(',').filter(Boolean).map(id => {
			const a = this.activities.find(x => String(x.activityId) === id.trim());
			return a ? `${a.emoji || ''} ${a.name}` : id;
		}).join(', ');
	}
}
